import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Container, Grid, Card, CircularProgress } from '@mui/material';
import BasicVideoModal from '../components/BasicVideoModal';
import { getSeasons } from '../services/api';
import {
  motion,
  useReducedMotion,
  useMotionValue,
  useTransform,
  useSpring,
} from 'framer-motion';

const MotionBox = motion(Box);

const EASE = [0.22, 1, 0.36, 1];
const GOLD = 'rgb(201,146,42)';

// Pre-computed stable particles (Halton-ish spread, no randomness at render time)
const PARTICLES = Array.from({ length: 22 }, (_, i) => ({
  x: ((i * 4.618) % 1) * 100,
  delay: ((i * 1.618) % 1) * 9,
  duration: 5 + ((i * 2.236) % 1) * 8,
  size: 1.5 + ((i * 1.732) % 1) * 2.5,
  opacity: 0.15 + ((i * 3.141) % 1) * 0.4,
}));

// ── Letter clip-reveal ───────────────────────────────────────────────────────
function RevealWord({ word, color, startDelay }) {
  return (
    <>
      {word.split('').map((char, i) => (
        <span
          key={i}
          style={{ display: 'inline-block', overflow: 'hidden', lineHeight: 'inherit', verticalAlign: 'top' }}
        >
          <motion.span
            initial={{ y: '110%' }}
            animate={{ y: 0 }}
            transition={{ duration: 0.75, ease: EASE, delay: startDelay + i * 0.058 }}
            style={{ display: 'inline-block', color, willChange: 'transform' }}
          >
            {char}
          </motion.span>
        </span>
      ))}
    </>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
const WorldCup2026 = () => {
  const prefersReducedMotion = useReducedMotion();

  const [nominations, setNominations] = useState([]);
  const [loadingNoms, setLoadingNoms] = useState(true);
  const [modalNom, setModalNom] = useState(null);

  useEffect(() => {
    getSeasons()
      .then((seasons) => {
        const wc = seasons.find((s) => s.name === 'World Cup 2026');
        setNominations(wc ? wc.nominations : []);
      })
      .catch(() => setNominations([]))
      .finally(() => setLoadingNoms(false));
  }, []);

  // Raw mouse position (–0.5 → +0.5 normalised)
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const smoothX = useSpring(rawX, { stiffness: 40, damping: 18 });
  const smoothY = useSpring(rawY, { stiffness: 40, damping: 18 });

  // Ghost text parallax — moves opposite to cursor
  const ghostX = useTransform(smoothX, [-0.5, 0.5], ['-28px', '28px']);
  const ghostY = useTransform(smoothY, [-0.5, 0.5], ['-18px', '18px']);

  // Cursor spotlight — fixed to viewport, offset so it's centred on cursor
  const spotRawX = useMotionValue(-600);
  const spotRawY = useMotionValue(-600);
  const spotX = useSpring(spotRawX, { stiffness: 80, damping: 22 });
  const spotY = useSpring(spotRawY, { stiffness: 80, damping: 22 });

  const handleMouseMove = useCallback(
    (e) => {
      if (prefersReducedMotion) return;
      rawX.set(e.clientX / window.innerWidth - 0.5);
      rawY.set(e.clientY / window.innerHeight - 0.5);
      spotRawX.set(e.clientX - 300);
      spotRawY.set(e.clientY - 300);
    },
    [prefersReducedMotion, rawX, rawY, spotRawX, spotRawY],
  );

  return (
    <>
    <Box
      onMouseMove={handleMouseMove}
      sx={{
        minHeight: '100vh',
        bgcolor: '#0A0A0A',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        pt: 8,
      }}
    >
      {/* ── Cursor spotlight ──────────────────────────────────────────────── */}
      {!prefersReducedMotion && (
        <MotionBox
          aria-hidden
          style={{ x: spotX, y: spotY }}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: 600,
            height: 600,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(201,146,42,0.09) 0%, transparent 62%)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      )}

      {/* ── Ambient pulse ─────────────────────────────────────────────────── */}
      <MotionBox
        aria-hidden
        initial={{ opacity: 0.12 }}
        animate={
          prefersReducedMotion
            ? {}
            : { scale: [1, 1.14, 1], opacity: [0.12, 0.22, 0.12] }
        }
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '65vw',
          height: '65vw',
          maxWidth: 860,
          maxHeight: 860,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(201,146,42,0.11) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* ── Floating ember particles ──────────────────────────────────────── */}
      {!prefersReducedMotion &&
        PARTICLES.map((p, i) => (
          <MotionBox
            key={i}
            aria-hidden
            initial={{ y: 0, opacity: 0 }}
            animate={{
              y: '-115vh',
              opacity: [0, p.opacity, p.opacity * 0.5, 0],
            }}
            transition={{
              duration: p.duration,
              delay: p.delay,
              repeat: Infinity,
              ease: 'linear',
            }}
            sx={{
              position: 'absolute',
              bottom: '5%',
              left: `${p.x}%`,
              width: p.size,
              height: p.size,
              borderRadius: '50%',
              bgcolor: GOLD,
              pointerEvents: 'none',
            }}
          />
        ))}

      {/* ── Ghost "2026" with mouse parallax ─────────────────────────────── */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        <MotionBox
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.8, ease: EASE }}
          style={prefersReducedMotion ? {} : { x: ghostX, y: ghostY }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: { xs: '35vw', md: '22vw' },
              lineHeight: 1,
              color: 'rgba(201,146,42,0.045)',
              whiteSpace: 'nowrap',
              letterSpacing: '-0.02em',
              display: 'block',
            }}
          >
            2026
          </Typography>
        </MotionBox>
      </Box>

      {/* ── Horizontal scan line ─────────────────────────────────────────── */}
      {!prefersReducedMotion && (
        <MotionBox
          aria-hidden
          initial={{ x: '-15vw' }}
          animate={{ x: '115vw' }}
          transition={{
            duration: 2.6,
            repeat: Infinity,
            repeatDelay: 6.5,
            ease: [0.4, 0, 0.6, 1],
            delay: 1.8,
          }}
          sx={{
            position: 'absolute',
            top: '50%',
            left: 0,
            width: '15vw',
            height: '1px',
            background: `linear-gradient(90deg, transparent, rgba(201,146,42,0.55), transparent)`,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      )}

      {/* ── Content ───────────────────────────────────────────────────────── */}
      <Container maxWidth={nominations.length > 0 ? 'lg' : 'md'} sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>

        {/* Eyebrow */}
        <MotionBox
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 3 }}
        >
          <MotionBox
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.3 }}
            sx={{ width: 24, height: 2, bgcolor: 'primary.main', borderRadius: '2px', transformOrigin: 'left center' }}
          />
          <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: '0.16em', fontSize: '0.65rem' }}>
            FIFA World Cup 2026
          </Typography>
          <MotionBox
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.3 }}
            sx={{ width: 24, height: 2, bgcolor: 'primary.main', borderRadius: '2px', transformOrigin: 'right center' }}
          />
        </MotionBox>

        {/* ── Nominations grid (live) ─────────────────────────────────── */}
        {!loadingNoms && nominations.length > 0 && (
          <Box sx={{ mt: 4, textAlign: 'left' }}>
            <Grid container spacing={2}>
              {nominations.map((nom) => (
                <Grid item xs={6} sm={4} md={3} key={nom.id}>
                  <Card
                    onClick={() => setModalNom(nom)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(201,146,42,0.15)',
                      borderRadius: 2,
                      overflow: 'hidden',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 32px rgba(0,0,0,0.4)' },
                    }}
                  >
                    <Box sx={{ aspectRatio: '16/9', bgcolor: '#111', overflow: 'hidden' }}>
                      {nom.thumbnail_url ? (
                        <img
                          src={nom.thumbnail_url}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <Box sx={{ width: '100%', height: '100%', background: 'linear-gradient(135deg,#1a1a1a,#2d2d2d)' }} />
                      )}
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.3, mb: 0.5, fontSize: '0.8rem' }}>
                        {nom.player_name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem', display: 'block' }}>
                        {nom.date}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* ── Hype content (no nominations yet) ──────────────────────── */}
        {!loadingNoms && nominations.length === 0 && (
        <>
        {/* "COMING" */}
        <Box
          role="heading"
          aria-level={1}
          aria-label="Coming Soon"
          sx={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: { xs: '17vw', sm: '12vw', md: '8rem' },
            lineHeight: 0.9,
            letterSpacing: '0.01em',
            mb: 0.5,
          }}
        >
          {prefersReducedMotion ? (
            <span style={{ color: '#fff' }}>Coming</span>
          ) : (
            <RevealWord word="COMING" color="#fff" startDelay={0.38} />
          )}
        </Box>

        {/* "SOON" */}
        <Box
          aria-hidden
          sx={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: { xs: '17vw', sm: '12vw', md: '8rem' },
            lineHeight: 0.9,
            letterSpacing: '0.01em',
            mb: 5,
          }}
        >
          {prefersReducedMotion ? (
            <span style={{ color: GOLD }}>Soon</span>
          ) : (
            <RevealWord word="SOON" color={GOLD} startDelay={0.84} />
          )}
        </Box>

        {/* Description */}
        <MotionBox
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.3 }}
        >
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.45)',
              maxWidth: 440,
              mx: 'auto',
              lineHeight: 1.7,
              fontSize: '0.9rem',
              mb: 4,
            }}
          >
            The World Cup&apos;s finest theatrical dives, ranked and voted on by you.
            Fallon&nbsp;d&apos;Floor returns for the biggest stage in football.
          </Typography>
        </MotionBox>

        {/* Divider */}
        <MotionBox
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ duration: 1, ease: EASE, delay: 1.5 }}
          sx={{
            width: 40,
            height: 1,
            bgcolor: 'rgba(255,255,255,0.12)',
            mx: 'auto',
            mb: 3,
            transformOrigin: 'center',
          }}
        />

        {/* Kickoff */}
        <MotionBox
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, ease: EASE, delay: 1.75 }}
        >
          <Typography
            variant="overline"
            sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.6rem', letterSpacing: '0.18em' }}
          >
            Kickoff 11 June 2026 &middot; USA &middot; Canada &middot; Mexico
          </Typography>
        </MotionBox>
        </>
        )}

        {loadingNoms && (
          <Box sx={{ mt: 6 }}>
            <CircularProgress size={24} sx={{ color: GOLD }} />
          </Box>
        )}
      </Container>
    </Box>

    {modalNom && (
      <BasicVideoModal
        open={Boolean(modalNom)}
        onClose={() => setModalNom(null)}
        nomination={modalNom}
      />
    )}
    </>
  );
};

export default WorldCup2026;
